/**
 * referralStatus.js
 * -----------------
 * Single source of truth for:
 * - Which form instances count toward "Started" vs "Completed"
 * - How we translate form progress + invite/account state into user-facing statuses
 *
 * Why this exists (real bug we hit):
 * - `ServiceUserDashboard` was updated to the new HFP (Holistic Functional Profiling) form keys.
 * - `PeoplePanel` (the “People involved in this referral” popup) still used the legacy SSN keys.
 * - Result: a school user could finish all current forms and see “Completed” on the dashboard,
 *   but the People popup would *never* reach "Completed" because it was waiting on forms that
 *   no longer exist — so it incorrectly showed "Started".
 *
 * By centralising the form-key lists and status calculation here, we avoid that drift.
 * A junior developer should be able to update this file *once* when form keys change,
 * and every UI surface stays consistent.
 */

import {
  PARTICIPANT_STATUS,
  PENDING_PARTICIPANT_STATUSES,
} from '../../constants/participantStatuses';
import { pathwayAdminHandover, pathwayReviews, pathwayTriage } from './triagePathways';
import { timestampToMillis } from '../../utils/timestampUtils';

/**
 * UI status values used across service-user surfaces.
 *
 * Notes on guardian flow:
 * - Guardians can exist in “invite sent” state before they have an account.
 * - We must show that as a distinct state so school users understand why nothing is happening yet.
 */
export const STATUS = {
  NOT_ASSIGNED: 'not_assigned',
  INVITE_SENT: 'invite_sent',
  NOT_STARTED: 'not_started',
  STARTED: 'started',
  COMPLETED: 'completed',
};

/**
 * Firestore formInstances keys that are required for a referral (both roles complete these).
 *
 * Some environments store role-suffixed versions (e.g. `requestForReferral_school_user`)
 * while others store an unsuffixed "base" doc (e.g. `requestForReferral`).
 *
 * In status calculations we always prefer role-suffixed if present, otherwise fall back to base.
 */
export const BASE_REFERRAL_FORMS = [
  'requestForReferral',
  'reasonForReferral',
  'educationHistory',
  'snapIV26',
];

/**
 * Base referral forms that are ADHD-specific. The SNAP-IV (snapIV26) is an ADHD
 * rating scale; it is only presented once an ADHD concern has been logged
 * (TD-139/TD-150) — 'adhd' or combined 'both' referrals keep it.
 */
export const ADHD_ONLY_REFERRAL_FORMS = ['snapIV26'];

// ── Confirm and submit (TDI-415 / TDI-418) ──────────────────────────────────

/**
 * Records created before the confirm-and-submit step shipped are promoted on
 * form completion alone, exactly as before: their respondents were promised
 * the referral is sent automatically once both sides finish, and holding them
 * to a confirmation step that did not exist when they started would strand
 * complete referrals. Records created on or after the cutover require every
 * respondent's confirmation. Mirrors _CONFIRMATION_CUTOVER in rms_forms.py —
 * keep in sync. Set to the release date: the confirm step shipped on
 * 19 October 2026.
 */
export const SUBMISSION_CONFIRMATION_CUTOVER_MS = Date.UTC(2026, 9, 19); // 19 Oct 2026

/** One stored confirm-and-submit map; anything malformed reads as empty. */
function rawRoleConfirmations(patient, field) {
  const confirmed = patient?.[field];
  return confirmed && typeof confirmed === 'object' && !Array.isArray(confirmed) ? confirmed : {};
}

/**
 * Whether the SEND pathway is, or has been, on this record (active, stopped
 * or withdrawn), or the record is a SEN-only Harbour pupil. While it is, the
 * strengths and needs forms are the SEND pathway's and are confirmed there.
 * Mirrors _record_has_sen_pathway.
 */
export function recordHasSenPathway(patient) {
  const status = String(patient?.senPathway?.status || '').trim().toLowerCase();
  if (status && status !== 'not_active') return true;
  return isHarbourPupilRecord(patient)
    && !patient?.intakeConcern && !patient?.referralPathway?.startedAt;
}

/** Whether the record holds its own instrument's strengths and needs
 * scoring. Mirrors _record_has_profile_scoring (and hasHfpReport's rule). */
function recordHasProfileScoring(patient) {
  const domains = (patient && patient.domains) || {};
  const scored = (key) => Boolean(domains[key] && domains[key].scoring);
  const instrument = hfpInstrumentForRecord(patient);
  if (instrument === 'v4') return scored('hfpV4');
  if (instrument === 'v3') return scored('hfpV3');
  return scored('hfpV2') || scored('hfpV3');
}

/**
 * Whether a confirmation stored under submissionConfirmed was in fact the
 * SEND pathway's: made, before confirmations went per pathway (TDI-651), on
 * a Harbour pupil who had no referral yet. Confirmations written since carry
 * a pathway marker and are never reinterpreted. Mirrors
 * _legacy_confirmation_was_sen in rms_forms.py — keep in step.
 */
function legacyConfirmationWasSen(patient, confirmation) {
  if (!confirmation || typeof confirmation !== 'object' || confirmation.pathway) return false;
  if (!isHarbourPupilRecord(patient)) return false;
  const referralActive = Boolean(patient?.intakeConcern) || Boolean(patient?.referralPathway?.startedAt);
  if (!referralActive) return true;
  const started = timestampToMillis(patient?.referralPathway?.startedAt);
  const confirmedAt = timestampToMillis(confirmation.confirmedAt);
  if (started == null || confirmedAt == null) return false;
  return confirmedAt < started;
}

/**
 * The per-role confirm-and-submit map for one pathway (server-write only,
 * recorded by rmsConfirmSubmission). Each pathway is confirmed on its own
 * (TDI-651, Tom King 27 Oct: the two pathways are completely separate):
 *
 *   'referral' — `submissionConfirmed.{role}`, the field the step has always
 *                written, so referral confirmations already on records keep
 *                their meaning.
 *   'sen'      — `senSubmissionConfirmed.{role}`.
 *
 * Two kinds of legacy confirmation count for the SEND pathway: one made on a
 * SEN-only record, and the old record-wide confirmations that released a
 * profile that exists (report email sent and the instrument's scoring
 * present), so that profile does not disappear. Mirrors
 * _submission_confirmations in rms_forms.py — keep in step.
 *
 * @param {object|null} patient - patientRecords doc data
 * @param {'referral'|'sen'} [pathway]
 */
export function submissionConfirmations(patient, pathway = 'referral') {
  const legacy = rawRoleConfirmations(patient, 'submissionConfirmed');
  if (pathway === 'sen') {
    const sen = { ...rawRoleConfirmations(patient, 'senSubmissionConfirmed') };
    const profileReleased = Boolean(patient?.hfpReportReadyNotifiedAt) && recordHasProfileScoring(patient);
    Object.entries(legacy).forEach(([role, confirmation]) => {
      if (sen[role] || !confirmation) return;
      const markerless = typeof confirmation === 'object' && !confirmation.pathway;
      if (legacyConfirmationWasSen(patient, confirmation) || (profileReleased && markerless)) {
        sen[role] = confirmation;
      }
    });
    return sen;
  }
  return Object.fromEntries(
    Object.entries(legacy).filter(([, c]) => c && !legacyConfirmationWasSen(patient, c))
  );
}

/** Whether one respondent has confirmed one pathway's forms. */
export function isRoleSubmissionConfirmed(patient, role, pathway = 'referral') {
  return Boolean(submissionConfirmations(patient, pathway)[role]);
}

/** Whether both respondents have confirmed one pathway's forms. Referral
 * readiness reads only the referral's confirmations (TDI-651). */
export function allRequiredRespondentsConfirmed(patient, pathway = 'referral') {
  const confirmed = submissionConfirmations(patient, pathway);
  return Boolean(confirmed.guardian && confirmed.school_user);
}

/**
 * Whether the respondents' confirmations release the strengths and needs
 * profile: both SEND confirmations on a record with a SEND pathway, else (the
 * referrals that collected those forms before 28 October 2026) both referral
 * confirmations. Mirrors _hfp_report_confirmations_in.
 */
export function hfpReportConfirmationsIn(patient) {
  return allRequiredRespondentsConfirmed(patient, recordHasSenPathway(patient) ? 'sen' : 'referral');
}

/**
 * Which pathway's confirmation covers a form instance: the strengths and
 * needs forms belong to the SEND pathway wherever the record has one; every
 * other form, and those forms on a record that never had a SEND pathway,
 * belong to the referral. Mirrors _instance_pathway.
 *
 * @param {object|null} patient
 * @param {string} instanceId - e.g. 'hfpV3Sensory_guardian'
 * @returns {'referral'|'sen'}
 */
export function instancePathway(patient, instanceId) {
  const base = String(instanceId || '').replace(/_(school_user|guardian)$/, '');
  const isHfp = base.startsWith(HFP_V4_PORTAL_FORM_KEY)
    || HFP_FORM_KEYS.includes(base)
    || HFP_V3_FORM_KEYS.includes(base)
    || HFP_V1_FORM_KEYS.includes(base);
  return isHfp && recordHasSenPathway(patient) ? 'sen' : 'referral';
}

/**
 * Whether this record is gated on respondent confirmations (TDI-418). Legacy
 * records (created before the cutover, or with no readable creation date)
 * keep the completion-is-the-decision rule. Mirrors the backend
 * _record_requires_confirmation — keep in sync, or the board shows a referral
 * as waiting while the server has already promoted it (or the reverse).
 */
export function recordRequiresSubmissionConfirmation(patient) {
  const ms = timestampToMillis(patient?.createdAt);
  return ms != null && ms >= SUBMISSION_CONFIRMATION_CUTOVER_MS;
}

/**
 * The base referral forms that apply to a referral given its intake concern.
 * The ADHD-only scale counts only once an ADHD concern is logged ('adhd' /
 * 'both'). An unset concern must NOT require it — the scale is not presented
 * until the concern is logged (TD-150), so requiring it would hold the
 * referral incomplete against a form the family cannot see. Mirrors the
 * backend _required_referral_bases (rms_forms.py) — keep in sync.
 *
 * @param {string|null} intakeConcern - 'asd' | 'adhd' | 'both' | null
 * @returns {string[]}
 */
export function effectiveBaseReferralForms(intakeConcern) {
  const c = String(intakeConcern || '').trim().toLowerCase();
  if (c === 'adhd' || c === 'both') {
    return BASE_REFERRAL_FORMS;
  }
  return BASE_REFERRAL_FORMS.filter((k) => !ADHD_ONLY_REFERRAL_FORMS.includes(k));
}

/**
 * HFP (Holistic Functional Profiling) form keys — the set assigned to
 * referral records that are NOT Harbour pupils (v2). Always role-suffixed in
 * Firestore (e.g. `hfpV2ExecutiveFunction_school_user`). Harbour pupils are
 * assigned HFP_V3_FORM_KEYS instead — pick per record with
 * hfpFormKeysForRecord rather than reading this list directly.
 *
 * IMPORTANT:
 * - This is the v2 question set (7 domains incl. Environmental). It supersedes
 *   the v1 HFP forms; the service-user journey assigns these forms and
 *   completion gating tracks them.
 * - Records carrying the v3 instrument hold HFP_V3_FORM_KEYS instances instead;
 *   the legacy v1 keys are retained in HFP_V1_FORM_KEYS for reference/backfill.
 * - If the product team adds/removes HFP forms, update these lists and tests.
 */
export const HFP_FORM_KEYS = [
  'hfpV2ExecutiveFunction',
  'hfpV2MotorPhysical',
  'hfpV2SpeechLanguageCommunication',
  'hfpV2SocialEmotional',
  'hfpV2Sensory',
  'hfpV2GlobalParticipation',
  'hfpV2EnvironmentalFactors',
];

/**
 * HFP v3 form keys — the 157-item instrument (same seven domains, role-suffixed
 * like v2). Records scored by the v3 pipeline (`domains.hfpV3`) hold these
 * instances; completion gating must recognise them so a v3-only referral does
 * not read as 0% HFP. Harbour pupils (SEN & ND Access Portal, TDI-53) are
 * assigned this set on both pathways; every other record keeps the v2 set
 * (see hfpFormKeysForRecord).
 */
export const HFP_V3_FORM_KEYS = [
  'hfpV3ExecutiveFunction',
  'hfpV3MotorPhysical',
  'hfpV3SpeechLanguageCommunication',
  'hfpV3SocialEmotional',
  'hfpV3Sensory',
  'hfpV3GlobalParticipation',
  'hfpV3EnvironmentalFactors',
];

/**
 * The one portal entry for the whole HFP v4 form (TDI-525). The v4 form is
 * one flow per respondent, stored across eight instances on the server
 * (hfp_v4/forms.py), so the portals list and count it as one form: the server
 * keeps formProgress.hfp4_{role} up to date as answers save and when the form
 * is sent (PORTAL_FORM_KEY in hfp_v4/forms.py).
 */
export const HFP_V4_PORTAL_FORM_KEY = 'hfp4';

/** HFP v4 form keys, as the portals and completion read them. */
export const HFP_V4_FORM_KEYS = [HFP_V4_PORTAL_FORM_KEY];

/**
 * The HFP instrument generations a record can be created on. Mirrors
 * _HFP_INSTRUMENTS in python-functions/rms_functions/rms_forms.py.
 */
export const HFP_INSTRUMENTS = ['v2', 'v3', 'v4'];

/**
 * Whether a patient record is a Harbour pupil (SEN & ND Access Portal,
 * TDI-53). Legacy referral records never carry the `harbour.pupil` flag.
 *
 * @param {object|null|undefined} patient - patientRecords doc data
 * @returns {boolean}
 */
export function isHarbourPupilRecord(patient) {
  return Boolean(patient && patient.harbour && patient.harbour.pupil);
}

/**
 * The date the REFERRAL started, which is not always the date the record was
 * created (Tom King, 27 October 2026). A Harbour pupil is added to the
 * school's portal first and referred later — sometimes much later, or never —
 * so `createdAt` is the day the SENCO listed the pupil, and the referral
 * starts when `referralPathway.startedAt` is written. Every other record is
 * created BY a referral submission, so its creation is the referral start.
 *
 * Clinical surfaces must show the referral's own date: the portal-add date
 * read as a referral date makes a referral look older than it is and skews
 * the service's waiting-time figures.
 *
 * @param {object|null|undefined} patient - patientRecords doc data
 * @returns {*} the referral's start timestamp in whatever form it is stored
 *   (Firestore Timestamp, ISO string, …), or null when there is none
 */
export function referralStartedAt(patient) {
  if (!patient) return null;
  const started = patient.referralPathway?.startedAt;
  if (started) return started;
  // A Harbour pupil with no referral has no referral date at all — never
  // fall back to the portal-add date, which is the bug this fixes.
  if (isHarbourPupilRecord(patient)) return null;
  return patient.createdAt ?? null;
}

/**
 * Whether the record's profile is pinned to an instrument: `hfpInstrument`,
 * written by the server the moment the first strengths and needs form opened
 * on the record (TDI-523). A profile in flight finishes on the instrument it
 * started on, and no report mixes instruments (Tom King, 28 October 2026).
 *
 * @param {object|null|undefined} patient - patientRecords doc data
 * @returns {boolean}
 */
export function hasStoredHfpInstrument(patient) {
  return Boolean(patient && HFP_INSTRUMENTS.includes(patient.hfpInstrument));
}

/**
 * The HFP instrument a record's profile is on. The one place in the browser
 * that answers it (TDI-523); _hfp_instrument_for_record in
 * python-functions/rms_functions/rms_helpers.py is the one place on the
 * server, and the two resolve identically — keep them in sync.
 *
 * In order:
 *   1. the record's pin (hasStoredHfpInstrument), once its first form opened;
 *   2. the organisation's setting (`rms_organisations/{orgId}.hfpInstrument`,
 *      read live by useOrgHfpInstrument) for a profile that has not started:
 *      the switch that moves an organisation to v4 (ruling D76, 16 Nov 2026);
 *   3. with no setting, the instrument each pathway used before the setting
 *      existed: v3 for a Harbour pupil, v2 for every other record. Dead the
 *      day every organisation carries the setting.
 *
 * Callers that only ever see started profiles (reports, scoring views) need
 * no setting: the pin answers.
 *
 * @param {object|null|undefined} patient - patientRecords doc data
 * @param {'v2'|'v3'|'v4'|null} [orgSetting] - the organisation's setting, when known
 * @returns {'v2'|'v3'|'v4'}
 */
export function hfpInstrumentForRecord(patient, orgSetting = null) {
  if (hasStoredHfpInstrument(patient)) return patient.hfpInstrument;
  if (HFP_INSTRUMENTS.includes(orgSetting)) return orgSetting;
  return isHarbourPupilRecord(patient) ? 'v3' : 'v2';
}

/**
 * The HFP form set of one instrument generation.
 *
 * @param {'v2'|'v3'|'v4'} instrument
 * @returns {string[]} HFP_V4_FORM_KEYS, HFP_V3_FORM_KEYS or HFP_FORM_KEYS (never a copy)
 */
export function hfpFormKeysForInstrument(instrument) {
  if (instrument === 'v4') return HFP_V4_FORM_KEYS;
  if (instrument === 'v3') return HFP_V3_FORM_KEYS;
  return HFP_FORM_KEYS;
}

/**
 * The HFP form set a record is ASSIGNED, from its instrument
 * (hfpInstrumentForRecord). This is the single selector for "which HFP forms
 * does this record ask for" — form lists, completion and status all route
 * through it, so a record is never shown one generation's forms while being
 * judged on another's.
 *
 * @param {object|null|undefined} patient - patientRecords doc data
 * @param {'v2'|'v3'|'v4'|null} [orgSetting] - the organisation's setting, when known
 * @returns {string[]} HFP_V4_FORM_KEYS, HFP_V3_FORM_KEYS or HFP_FORM_KEYS (never a copy)
 */
export function hfpFormKeysForRecord(patient, orgSetting = null) {
  return hfpFormKeysForInstrument(hfpInstrumentForRecord(patient, orgSetting));
}

/**
 * Whether a form key is an HFP form of either current generation (v2 or v3).
 * For surfaces that only know the form key (e.g. the form page deciding its
 * layout), not the record.
 *
 * @param {string} formKey - base (unsuffixed) form key
 * @returns {boolean}
 */
export function isHfpFormKey(formKey) {
  return HFP_FORM_KEYS.includes(formKey) || HFP_V3_FORM_KEYS.includes(formKey);
}

/**
 * Legacy v1 HFP form keys. Retained for any in-flight referrals that still hold
 * v1 form instances / `domains.hfp` scoring. Not used for new-referral gating.
 */
export const HFP_V1_FORM_KEYS = [
  'hfpThinkingAndLearning',
  'hfpAttentionAndMemory',
  'hfpEnergyAndMotivation',
  'hfpEmotionsAndRelationships',
  'hfpLanguageAndCommunication',
  'hfpMovementAndCoordination',
  'hfpSensoryExperiences',
  'hfpDailyLifeParticipation',
];

/**
 * Post-acceptance triage forms (guardian only when assigned).
 *
 * IMPORTANT (real-world data shape):
 * - In Firestore, these forms are stored as formInstances using the **schema formKey**
 *   (camelCase), and in most flows they are **role-suffixed** (e.g. `theirEarlyLife_guardian`).
 * - Some legacy/test data may be unsuffixed (e.g. `theirEarlyLife`).
 *
 * Because both shapes can exist, status calculations must treat either as valid.
 */
export const TRIAGE_FORM_KEYS = [
  'theirEarlyLife',
  'theirFamilyAndHomeLife',
  'theirLifestyle',
  'theirHistoryWithTheLaw',
  'theirFamilyHistory',
  'theirMedicalHistory',
  'theirMentalHealthHistory',
];

/**
 * Returns true if a Firestore `formInstances/{docId}` key represents any triage form.
 *
 * We accept both:
 * - base formKey (legacy/test): `theirEarlyLife`
 * - role-suffixed (common): `theirEarlyLife_guardian`
 */
export function isTriageFormInstanceKey(key) {
  if (!key) return false;
  return TRIAGE_FORM_KEYS.some((baseKey) => key === baseKey || key === `${baseKey}_guardian`);
}

/**
 * Shared badge labelling + styling. Centralised so pages don’t drift.
 */
export const STATUS_BADGE_CONFIG = {
  [STATUS.NOT_ASSIGNED]: {
    label: 'Not Assigned',
    className: 'bg-gray-100 text-gray-500 border-gray-200',
  },
  [STATUS.INVITE_SENT]: {
    label: 'Invite Sent',
    className: 'bg-purple-100 text-purple-700 border-purple-200',
  },
  [STATUS.NOT_STARTED]: {
    label: 'Not Started',
    className: 'bg-slate-100 text-slate-600 border-slate-200',
  },
  [STATUS.STARTED]: {
    label: 'Started',
    className: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  [STATUS.COMPLETED]: {
    label: 'Completed',
    className: 'bg-green-100 text-green-700 border-green-200',
  },
};

/**
 * Convenience getter so components can stay defensive if they receive an unknown status.
 */
export function getStatusBadgeConfig(status) {
  return STATUS_BADGE_CONFIG[status] || STATUS_BADGE_CONFIG[STATUS.NOT_STARTED];
}

function isNumber(n) {
  return typeof n === 'number' && !Number.isNaN(n);
}

/**
 * Normalise a stored progress object into a simple numeric percent.
 * We treat missing / invalid values as 0.
 *
 * @param {Record<string, number>} formProgress - map of formKey -> percent (0..100)
 * @param {string} key - formInstances document key
 */
function getPercent(formProgress, key) {
  const raw = formProgress?.[key];
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return isNumber(n) ? n : 0;
}

/**
 * Pick the HFP form-key set a role is judged on.
 *
 * Three instrument generations can appear on records: v3 (`hfpV3*`, the
 * 157-item instrument Harbour pupils are assigned), v2 (`hfpV2*`, what every
 * other referral is assigned), and legacy v1 (`hfpAttentionAndMemory`, …).
 * A record seeded on a generation other than its assigned one must still be
 * recognised — otherwise a fully-completed referral on another set reads as
 * 0% HFP and can never leave "Incomplete Referrals" (and, since triage is
 * gated on referral completeness, disappears from "Pending 1st Review").
 *
 * The rule mirrors the backend exactly (rms_forms.py `_hfp_keys_for_role`),
 * so the board never disagrees with the server:
 * - A record with a stored instrument (TDI-414) is judged on that set ONLY;
 *   instances from another generation never count towards or against it.
 * - Harbour pupils are judged on the v3 set ONLY (hfpFormKeysForRecord): v2
 *   instances on a Harbour record never count, so the status always matches
 *   the forms the Harbour homepages list.
 * - Every other record is judged on its assigned v2 set. Only when the role
 *   holds no v2 instance at all does it fall back to whichever generation it
 *   does hold (v3, then legacy v1), so records seeded on another generation
 *   are not held incomplete against forms nobody is asked to fill in.
 * - A role with no HFP instances at all is judged on the assigned set, so a
 *   genuinely missing HFP counts as incomplete rather than silently passing.
 *
 * Callers that hold the record pass it as `patient`; without it the record is
 * treated as a legacy (non-Harbour) record.
 *
 * @param {'school_user'|'guardian'} role
 * @param {Record<string, number>} formProgress - formInstanceKey → percent
 * @param {object|null} [patient] - patientRecords doc data, when known
 * @returns {string[]} the base HFP keys to check for this role
 */
export function hfpKeysForRoleData(role, formProgress, patient, orgSetting = null) {
  const assigned = hfpFormKeysForRecord(patient, orgSetting);
  // A pinned profile (TDI-523) is judged on its instrument alone. Mirrors
  // _hfp_keys_for_role in python-functions/rms_functions/rms_forms.py.
  if (hasStoredHfpInstrument(patient)) return assigned;
  const hasData = (keys) =>
    keys.some(
      (k) => formProgress && Object.prototype.hasOwnProperty.call(formProgress, `${k}_${role}`),
    );
  if (hasData(assigned)) return assigned;
  if (hasData(HFP_V3_FORM_KEYS)) return HFP_V3_FORM_KEYS;
  if (hasData(HFP_V1_FORM_KEYS)) return HFP_V1_FORM_KEYS;
  return assigned;
}

/**
 * Display titles for every HFP form key across all three instrument
 * generations. v2 and v3 share the same seven domain names; v1 names follow
 * the legacy service-user labels (matching riskStratification's map).
 */
export const HFP_FORM_TITLES = {
  // v3 (157-item instrument) — same seven domains as v2
  hfpV3ExecutiveFunction: 'Executive Function',
  hfpV3MotorPhysical: 'Motor & Physical',
  hfpV3SpeechLanguageCommunication: 'Speech, Language & Communication',
  hfpV3SocialEmotional: 'Social & Emotional',
  hfpV3Sensory: 'Sensory',
  hfpV3GlobalParticipation: 'Global Participation',
  hfpV3EnvironmentalFactors: 'Environmental Factors',
  // v2 (current service-user question set)
  hfpV2ExecutiveFunction: 'Executive Function',
  hfpV2MotorPhysical: 'Motor & Physical',
  hfpV2SpeechLanguageCommunication: 'Speech, Language & Communication',
  hfpV2SocialEmotional: 'Social & Emotional',
  hfpV2Sensory: 'Sensory',
  hfpV2GlobalParticipation: 'Global Participation',
  hfpV2EnvironmentalFactors: 'Environmental Factors',
  // v1 (legacy)
  hfpThinkingAndLearning: 'Thinking & Learning',
  hfpAttentionAndMemory: 'Attention & Memory',
  hfpEnergyAndMotivation: 'Energy & Motivation',
  hfpEmotionsAndRelationships: 'Emotions & Relationships',
  hfpLanguageAndCommunication: 'Language & Communication',
  hfpMovementAndCoordination: 'Movement & Coordination',
  hfpSensoryExperiences: 'Sensory Experiences',
  hfpDailyLifeParticipation: 'Daily Life Participation',
};

/**
 * The HFP form rows a surface should DISPLAY for a role, derived from the same
 * key-set detection the completion counters use (hfpKeysForRoleData). Any list
 * of HFP forms shown next to a completion count must come from here —
 * otherwise the rows and the count can disagree (a static v2 list showed every
 * HFP form as 0%/no-data on v3-only records while the header counted them
 * complete, and did the same on legacy v1 records).
 *
 * @param {'school_user'|'guardian'} role
 * @param {Record<string, number>} formProgress - formInstanceKey → percent
 * @param {object|null} [patient] - patientRecords doc data (Harbour pupils read v3)
 * @returns {{key: string, title: string}[]}
 */
export function hfpFormsForRoleData(role, formProgress, patient, orgSetting = null) {
  return hfpKeysForRoleData(role, formProgress, patient, orgSetting).map((key) => ({
    key,
    title: HFP_FORM_TITLES[key] || key,
  }));
}

/**
 * completionPercent
 * -----------------
 * Normalise a form instance's status + raw progress into a completion-accurate
 * percent for every "is this form/section done?" decision.
 *
 * INVARIANT enforced here (single source of truth): **100 means submitted.**
 * A form can be drafted all the way to 100% via auto-save without the user ever
 * clicking Submit. If that draft 100 were treated as complete it would (wrongly)
 * turn the section green, unlock the next section, flip the referral to
 * "Completed", and let the user open a `?mode=review` page for an instance that
 * was never submitted (the "Live schema"/review errors testers saw). To prevent
 * all of that, draft progress is capped at 99 — only an actual `submitted`
 * status yields 100.
 *
 * Build every progress map fed to the completion predicates through this helper.
 *
 * @param {string|undefined} status - form instance status ('draft' | 'submitted')
 * @param {number|string|undefined} rawPercent - stored progress percent (0..100)
 * @returns {number} 100 if submitted, else the raw percent clamped to [0, 99]
 */
export function completionPercent(status, rawPercent) {
  if (status === 'submitted') return 100;
  const n = Number(rawPercent);
  if (!Number.isFinite(n)) return 0;
  return Math.min(Math.max(n, 0), 99);
}

/**
 * buildFormProgressFromSnapshot
 * -----------------------------
 * Fold a formInstances query snapshot into `{ formProgress, triageAssigned }`,
 * applying the completionPercent invariant to EVERY instance: a submitted form
 * is 100 even when its stored progress map is missing or stale, and a draft is
 * capped at 99 no matter what percent it stored.
 *
 * This is the single reader for instance progress. Every surface that folds a
 * snapshot itself and reads only `progress.percent` will mislabel submitted
 * forms (e.g. the guardian's dashboard showed the school as "Not Started" on
 * first login even though the school had submitted everything).
 *
 * @param {{ forEach: Function }} qs - Firestore QuerySnapshot (or anything
 *   exposing forEach over docs with `.id` and `.data()`)
 * @returns {{ formProgress: Record<string, number>, triageAssigned: boolean }}
 */
export function buildFormProgressFromSnapshot(qs) {
  const formProgress = {};
  let triageAssigned = false;
  qs.forEach((docSnap) => {
    const d = (typeof docSnap.data === 'function' ? docSnap.data() : docSnap.data) || {};
    const key = docSnap.id || '';
    if (!key) return;
    formProgress[key] = completionPercent(
      d.status,
      d.progress?.percent ?? d.progress?.progressPercentage
    );
    if (isTriageFormInstanceKey(key)) triageAssigned = true;
  });
  return { formProgress, triageAssigned };
}

/**
 * buildFormProgressFromRecord
 * ---------------------------
 * Fold a patient record's `formProgress` mirror into the same
 * `{ formProgress, triageAssigned }` shape buildFormProgressFromSnapshot
 * produces from a formInstances snapshot.
 *
 * Why it exists (TDI-154): client reads of the formInstances subcollection are
 * provider-only — the instances carry each party's confidential answers, while
 * every service-user surface only ever needed headline status/percent. The
 * backend mirrors that headline onto the record as
 * `formProgress.{instanceId} = { status, percent, role, updatedAt }`, which
 * participants can already read. Service-user surfaces fold THIS map; provider
 * surfaces keep folding the live snapshot.
 *
 * The completionPercent invariant applies identically: a submitted instance is
 * 100 even when its mirrored percent is missing or stale, and a draft is capped
 * at 99. Keys are the raw instance ids (role-suffixed, with rare legacy
 * unsuffixed ids), exactly as the snapshot fold produces them, so every
 * downstream helper (calculateRoleStatus, areAllTriageFormsComplete, …) works
 * unchanged. A record without the mirror (pre-backfill) folds to an empty map —
 * every form reads as not started, never a crash.
 *
 * @param {object|null|undefined} record - patientRecords doc data
 * @returns {{ formProgress: Record<string, number>, triageAssigned: boolean }}
 */
export function buildFormProgressFromRecord(record) {
  const formProgress = {};
  let triageAssigned = false;
  const mirror =
    record && typeof record.formProgress === 'object' && record.formProgress !== null
      ? record.formProgress
      : {};
  Object.keys(mirror).forEach((key) => {
    if (!key) return;
    const entry = mirror[key] || {};
    formProgress[key] = completionPercent(entry.status, entry.percent);
    if (isTriageFormInstanceKey(key)) triageAssigned = true;
  });
  return { formProgress, triageAssigned };
}

/**
 * resolveRoleFormInstance
 * -----------------------
 * Resolve the Firestore formInstances doc backing a logical form for a role.
 * Instances are stored role-suffixed (`{baseKey}_{role}`) in every current
 * flow (referral, HFP, triage — rmsAssignTriageForms writes
 * `theirEarlyLife_guardian`, etc.); some legacy/test data holds a single
 * unsuffixed base doc. Suffixed-first with a bare fallback covers both.
 *
 * Every surface that resolves an instance by hand risks the drift we hit on
 * the provider FormProgressDialog: its count helper looked up triage forms
 * unsuffixed, missed all seven, and reported "4 of 11 complete" while the
 * rows (suffixed lookup) showed green.
 *
 * @param {Record<string, object>|null|undefined} formInstances
 * @param {'guardian'|'school_user'} role
 * @param {string} baseKey - logical (unsuffixed) form key
 * @returns {object|null}
 */
export function resolveRoleFormInstance(formInstances, role, baseKey) {
  if (!formInstances || !baseKey) return null;
  return formInstances[`${baseKey}_${role}`] || formInstances[baseKey] || null;
}

/**
 * Whether a role has submitted every one of the given forms, judged from a raw
 * formInstances map of `{ [instanceId]: { status, percent } }` (as built from
 * the realtime snapshot). Used to derive the OTHER party's completion live
 * (TD-156), so the "waiting for the other party" message updates the moment
 * they finish instead of going stale after a one-shot fetch.
 *
 * Prefers the role-suffixed instance (`{key}_{role}`); falls back to a
 * non-suffixed instance for forms without role variants. Submit-driven: a
 * fully-drafted-but-unsubmitted form does not count (completionPercent).
 *
 * @param {Record<string, {status?: string, percent?: number}>|null|undefined} instanceProgress
 * @param {string[]} formKeys - camelCase form keys the role must complete
 * @param {'school_user'|'guardian'} role
 * @returns {boolean}
 */
export function areRoleInstancesComplete(instanceProgress, formKeys, role) {
  if (!instanceProgress || !Array.isArray(formKeys) || formKeys.length === 0 || !role) {
    return false;
  }
  return formKeys.every((formKey) => {
    const inst = instanceProgress[`${formKey}_${role}`] ?? instanceProgress[formKey];
    if (!inst) return false;
    return completionPercent(inst.status, inst.percent) >= 100;
  });
}

/**
 * Build the list of progress percentages that count toward completion for a given role.
 * This is intentionally "dumb" and predictable: missing forms count as 0.
 */
function buildRoleProgressList({ role, formProgress, triageAssigned, intakeConcern }) {
  const progress = [];

  // Base referral forms: prefer role-suffixed if present, else fall back to base.
  // Autism-only referrals drop the ADHD SNAP-IV scale (TD-139).
  for (const baseKey of effectiveBaseReferralForms(intakeConcern)) {
    const roleSuffixKey = `${baseKey}_${role}`;
    if (formProgress && Object.prototype.hasOwnProperty.call(formProgress, roleSuffixKey)) {
      progress.push(getPercent(formProgress, roleSuffixKey));
    } else if (formProgress && Object.prototype.hasOwnProperty.call(formProgress, baseKey)) {
      progress.push(getPercent(formProgress, baseKey));
    } else {
      progress.push(0);
    }
  }

  // No HFP here. The strengths and needs forms belong to the SEND pathway and
  // to no referral (Tom King, 27 and 28 October 2026; Diana's referral forms
  // homepages carry four school forms and eleven parent forms, with no HFP on
  // either). calculateSenRoleStatus judges them on their own pathway, which is
  // where the instrument choice between v2, v3 and v4 still matters.
  //
  // Secondary-phase referrals used to be the exception that skipped them
  // (TD-129/TD-137); now every referral does, so the special case is gone.

  // Triage forms: guardian only, and only once assigned.
  if (role === 'guardian' && triageAssigned) {
    for (const baseKey of TRIAGE_FORM_KEYS) {
      // Prefer role-suffixed if present, else fall back to base.
      const roleSuffixKey = `${baseKey}_${role}`;
      if (formProgress && Object.prototype.hasOwnProperty.call(formProgress, roleSuffixKey)) {
        progress.push(getPercent(formProgress, roleSuffixKey));
      } else if (formProgress && Object.prototype.hasOwnProperty.call(formProgress, baseKey)) {
        progress.push(getPercent(formProgress, baseKey));
      } else {
        progress.push(0);
      }
    }
  }

  return progress;
}

/**
 * Calculate the display status for a role given progress + guardian invite state.
 *
 * Intended usage:
 * - Service user dashboard tables
 * - People-involved popups/panels
 *
 * @param {Object} args
 * @param {'school_user'|'guardian'} args.role
 * @param {Record<string, number>} args.formProgress - formKey -> percent
 * @param {boolean} args.triageAssigned
 * @param {boolean} [args.hasGuardian] - only meaningful when role === 'guardian'
 * @param {string|null} [args.guardianInviteStatus] - participant.status for guardian (e.g. 'invite_sent')
 * @param {string|null} [args.intakeConcern] - 'asd' | 'adhd' | 'both'; asd-only excludes the SNAP-IV scale
 * @param {string|null} [args.schoolPhase] - accepted and ignored since TDI-495; no referral asks for the HFP,
 *   so the phase no longer changes what a referral requires
 * @param {object|null} [args.patient] - accepted and ignored since TDI-495; the record's HFP instrument is
 *   read by the SEND pathway's own helpers (calculateSenRoleStatus)
 */
export function calculateRoleStatus({
  role,
  formProgress,
  triageAssigned,
  hasGuardian,
  guardianInviteStatus,
  intakeConcern,
  // schoolPhase and patient are still passed by several callers and are
  // deliberately not read: since TDI-495 neither decides what a referral
  // asks for. They stay in the documented signature so those call sites keep
  // working unchanged.
}) {
  // Guardian has additional states that happen before forms are even possible.
  if (role === 'guardian') {
    if (!hasGuardian) return STATUS.NOT_ASSIGNED;
    if (PENDING_PARTICIPANT_STATUSES.includes(guardianInviteStatus)) {
      return STATUS.INVITE_SENT;
    }
  }

  const roleFormProgress = buildRoleProgressList({
    role, formProgress, triageAssigned, intakeConcern,
  });
  const allComplete = roleFormProgress.length > 0 && roleFormProgress.every((p) => p === 100);
  const anyStarted = roleFormProgress.some((p) => p > 0);

  if (allComplete) return STATUS.COMPLETED;
  if (anyStarted) return STATUS.STARTED;
  return STATUS.NOT_STARTED;
}

// ─────────────────────────────────────────────────────────────────────────────
// Referral Stage Classification
// ─────────────────────────────────────────────────────────────────────────────
// These helpers power the provider dashboard's side-navigation, which groups
// referrals into 7 sub-stages across three overarching phases:
//   Pre-Triage  → Incoming Referrals  (stages 1-3)
//   Triage      → Ready for Triage    (stages 4-5)
//   Post-Triage → Triage Complete     (stages 6-7)
//
// The classification is based on form-completion data (from formInstances) and
// patient-record-level flags (triageFormsAssigned, triageDecision).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * isRoleReferralFormsComplete
 * ---------------------------
 * Whether this role has finished every form the referral asks of them.
 *
 * The strengths and needs forms are not among them. They are the SEND
 * pathway's work and belong to no referral: Diana's referral forms homepages
 * list four school forms and eleven parent forms, with no HFP on either, and
 * Tom King ruled on 27 October 2026 that "the SEND pathway and the referral
 * pathway are completely separate, so SEND forms must never block a referral
 * being submitted" — a rule with no exception for a child who happens not to
 * be on the SEND pathway.
 *
 * Until 28 October this function required the HFP set from both roles unless
 * the referral was secondary phase, which is why a referral-only family was
 * asked for seven forms, 145 questions and about 75 minutes that were never
 * theirs to answer.
 *
 * Triage forms are excluded here too; they are checked separately where the
 * guardian's own completeness is judged.
 *
 * @param {'school_user'|'guardian'} role - The respondent role to check
 * @param {Record<string, number>} formProgress - formInstanceKey → progress percent
 * @param {string|null} [intakeConcern] - 'asd' | 'adhd' | 'both'; asd-only excludes the SNAP-IV scale
 * @returns {boolean}
 */
export function isRoleReferralFormsComplete(role, formProgress, intakeConcern) {
  if (!formProgress || !role) return false;

  // Base referral forms: prefer role-suffixed key, fall back to unsuffixed (legacy data).
  // Autism-only referrals drop the ADHD SNAP-IV scale (TD-139).
  for (const baseKey of effectiveBaseReferralForms(intakeConcern)) {
    const suffixed = `${baseKey}_${role}`;
    let percent = 0;
    if (Object.prototype.hasOwnProperty.call(formProgress, suffixed)) {
      percent = getPercent(formProgress, suffixed);
    } else if (Object.prototype.hasOwnProperty.call(formProgress, baseKey)) {
      percent = getPercent(formProgress, baseKey);
    }
    if (percent < 100) return false;
  }

  return true;
}

/**
 * @deprecated Use isRoleReferralFormsComplete. Kept so a caller written before
 * 28 October 2026 cannot silently go on gating a referral on the SEND
 * pathway's forms; the extra arguments are ignored.
 */
export function isRoleReferralHfpComplete(role, formProgress, _schoolPhase, intakeConcern) {
  return isRoleReferralFormsComplete(role, formProgress, intakeConcern);
}

/**
 * areAllTriageFormsComplete
 * -------------------------
 * Returns true when ALL seven triage forms have 100% progress for the guardian.
 * These forms are guardian-only and part of the referral, not something
 * that follows acceptance (Tom King, 28 October 2026).
 *
 * @param {Record<string, number>} formProgress - formInstanceKey → progress percent
 * @returns {boolean}
 */
export function areAllTriageFormsComplete(formProgress) {
  if (!formProgress) return false;

  for (const baseKey of TRIAGE_FORM_KEYS) {
    const guardianKey = `${baseKey}_guardian`;
    let percent = 0;
    if (Object.prototype.hasOwnProperty.call(formProgress, guardianKey)) {
      percent = getPercent(formProgress, guardianKey);
    } else if (Object.prototype.hasOwnProperty.call(formProgress, baseKey)) {
      percent = getPercent(formProgress, baseKey);
    }
    if (percent < 100) return false;
  }

  return true;
}

/**
 * getTriageFormsProgress
 * ----------------------
 * Returns a summary of how many triage forms are complete out of the total 7.
 * Used by the "Assessment Requested" stage table to display a progress indicator.
 *
 * @param {Record<string, number>} formProgress - formInstanceKey → progress percent
 * @returns {{ completed: number, total: number }}
 */
export function getTriageFormsProgress(formProgress) {
  const total = TRIAGE_FORM_KEYS.length;
  if (!formProgress) return { completed: 0, total };

  let completed = 0;
  for (const baseKey of TRIAGE_FORM_KEYS) {
    const guardianKey = `${baseKey}_guardian`;
    let percent = 0;
    if (Object.prototype.hasOwnProperty.call(formProgress, guardianKey)) {
      percent = getPercent(formProgress, guardianKey);
    } else if (Object.prototype.hasOwnProperty.call(formProgress, baseKey)) {
      percent = getPercent(formProgress, baseKey);
    }
    if (percent >= 100) completed++;
  }

  return { completed, total };
}

/**
 * lastActivityLabel
 * -----------------
 * Human-readable dwell time for the support view of incomplete referrals
 * (LSC-036): how long since anyone last worked on the referral's forms.
 * Whole calendar-agnostic days (24h blocks) — precise enough for chasing
 * decisions, and never claims more precision than the data has.
 *
 * @param {number|null|undefined} millis - epoch millis of the last activity
 * @param {number} [nowMillis] - injectable clock for tests
 * @returns {string|null} 'Today' | 'Yesterday' | 'N days ago', or null when unknown
 */
export function lastActivityLabel(millis, nowMillis = Date.now()) {
  if (!Number.isFinite(millis) || millis <= 0) return null;
  const days = Math.floor((nowMillis - millis) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

/**
 * classifyReferralStage
 * ---------------------
 * Determines which dashboard sub-stage a referral belongs to, based on form
 * completion data and patient-record-level flags.
 *
 * Classification runs from the most advanced stage backwards so that a referral
 * is always placed in the furthest stage it qualifies for:
 *
 *   Post-triage (decided AND the outcome letter signed off) → Pending 1st
 *   Review (decided but the letter not signed off yet, or marked Ready for
 *   triage, or already locked for triage) → Forms Complete (everything
 *   submitted, awaiting the ready decision) → Forms In Progress (default)
 *
 * Reaching the triage queue always requires the explicit Ready-for-triage
 * decision (LSC-038/LSC-044) — form completion alone is never enough.
 *
 * @param {Object} patient - The patient record document from Firestore
 * @param {Object|null} completionData - Output of useCompletionDataForPatients for this patient
 * @returns {string} The sub-stage ID matching a REFERRAL_STAGES subStage.id
 */
/**
 * The outcome-letter container for a pathway.
 *
 * A combined referral keeps one letter per pathway under
 * triagePathways.{pathway}.triageDocuments; a single-concern referral keeps its
 * one letter in the legacy top-level triageDocuments.
 */
function outcomeLetterDocs(patient, pathway) {
  if (pathway) {
    const scoped = patient?.triagePathways?.[pathway]?.triageDocuments;
    if (scoped) return scoped;
  }
  return patient?.triageDocuments || null;
}

/**
 * Has the outcome letter actually gone to its recipients?
 *
 * sentAt is stamped by rmsPublishTriageLetter; sharedRoles is checked too
 * because letters shared to a portal role before the timestamp existed carry
 * the roles but no date.
 */
export function isOutcomeLetterSent(patient, pathway) {
  const docs = outcomeLetterDocs(patient, pathway);
  if (!docs) return false;
  return Boolean(docs.sentAt) || (docs.sharedRoles || []).length > 0;
}

/** Has a clinician signed the outcome letter off, handing it to the admin team? */
export function hasSignedOffLetter(patient, pathway) {
  return Boolean(outcomeLetterDocs(patient, pathway)?.signOff);
}

/**
 * Whether a decided referral is waiting on its outcome letter's sign-off
 * (TDI-395): the explicit state the decision writes (letterSignOff:
 * 'awaiting', cleared by the sign-off), or on older records a decided triage
 * whose letter is not signed off yet.
 */
export function isAwaitingLetterSignOff(patient, pathway) {
  const triage = pathwayTriage(patient, pathway) || null;
  if (triage?.status !== 'complete') return false;
  return triage.letterSignOff === 'awaiting'
    || (triage.letterSignOff == null && !hasSignedOffLetter(patient, pathway));
}

/**
 * Referrals waiting on a letter sign-off first, everything else after, each
 * group keeping the order it came in (the table's chosen sort), so a decided
 * referral is never lost below the ones still to triage.
 */
export function pinAwaitingLetterSignOff(patients, pathway) {
  const list = patients || [];
  const pinned = list.filter((p) => isAwaitingLetterSignOff(p, pathway));
  if (!pinned.length) return list;
  return [...pinned, ...list.filter((p) => !isAwaitingLetterSignOff(p, pathway))];
}

/**
 * The clinician an outcome letter is waiting on: the letter's assigned
 * reviewer (the clinician whose decision wrote it, the only one who may sign
 * it off), else the deciding clinician on the latest review. Null when
 * neither is recorded.
 */
export function letterSignOffClinician(patient, pathway) {
  const docs = outcomeLetterDocs(patient, pathway);
  if (docs?.assignedReviewerName) return docs.assignedReviewerName;
  const reviews = pathwayReviews(patient, pathway);
  const latest = reviews.length ? reviews[reviews.length - 1] : null;
  return latest?.decidedBy?.clinicianName || null;
}

/**
 * Referrals whose letter went out before Handover complete existed (TDI-391).
 *
 * They were finished under the old rule, where sending the letter was what
 * moved a child to Accepted or Not Accepted. Judging them by the new rule
 * would pull every one of them back into Admin Handover for EMIS work that
 * was done weeks ago, so they count as handed over. Mirrors
 * _HANDOVER_COMPLETE_CUTOVER in rms_admin_handover.py — keep in sync.
 */
export const HANDOVER_COMPLETE_CUTOVER_MS = Date.UTC(2026, 9, 28); // 28 Oct 2026

/**
 * Has admin finished the EMIS work and pressed Handover complete?
 *
 * Sending the letter is no longer what finishes a referral: the child leaves
 * Admin Handover when the work behind it is done, not when the post goes out
 * (TDI-391). Pre-cutover letters are the exception above.
 */
export function isHandoverComplete(patient, pathway) {
  if (pathwayAdminHandover(patient, pathway)?.completedAt) return true;
  if (!isOutcomeLetterSent(patient, pathway)) return false;
  // A letter sent under the old rule is already finished. Letters shared to a
  // portal role before sentAt existed carry no date at all, and must not be
  // dragged back into the list either — "no date" means "long ago", not "now".
  const sentMs = timestampToMillis(outcomeLetterDocs(patient, pathway)?.sentAt);
  return sentMs == null || sentMs < HANDOVER_COMPLETE_CUTOVER_MS;
}

/**
 * Whether the family's consent for the referral has been withdrawn and not
 * given again (TDI-472/478). The engine's own record wins; referrals withdrawn
 * before it existed carry only the stop reason. Mirrors is_referral_withdrawn
 * in rms_consent_withdrawal.py — keep in sync.
 */
export function isReferralWithdrawn(patient) {
  const withdrawal = (patient || {}).consentWithdrawal || {};
  if (withdrawal.restoredAt) return false;
  if (withdrawal.withdrawnAt) return true;
  return ((patient || {}).referralPathway || {}).stopReason === 'consent_withdrawn';
}

export function classifyReferralStage(patient, completionData, pathway) {
  // A withdrawn referral is in no clinician's list (TDI-472). It has one
  // place of its own, read-only, for the support side to answer a family or
  // the Trust (TDI-481). Decided before anything else: whatever stage the
  // referral had reached, the withdrawal ended it.
  if (isReferralWithdrawn(patient)) return 'consent-withdrawn';

  const formProgress = completionData?.formProgress || {};

  // Autism-only referrals drop the ADHD SNAP-IV scale from the base forms (TD-139).
  const intakeConcern = patient?.intakeConcern || null;

  // Triage forms are "assigned" if either the patient-record flag is set (written by
  // the rmsAssignTriageForms Cloud Function) or we detect triage form instances in
  // the formInstances subcollection.
  const triageFormsAssigned =
    Boolean(patient?.triageFormsAssigned) || Boolean(completionData?.triageAssigned);

  // triageReviews is an ordered array of clinician review objects written by the
  // rmsSubmitTriageDecision Cloud Function. Each entry has at minimum:
  //   { accepted: bool, decidedBy: { clinicianId, clinicianName }, decidedAt }
  // An empty/missing array means no clinician has reviewed yet.
  // C8: for a combined referral, classify by the requested pathway's reviews
  // (independent triage tracks); otherwise use the legacy shared reviews.
  const reviews = pathway
    ? pathwayReviews(patient, pathway)
    : (Array.isArray(patient?.triageReviews) ? patient.triageReviews : []);

  // The pathway's terminal status distinguishes a single-decision flow (ADHD:
  // the backend marks the pathway 'complete' on accept OR reject) from the
  // autism dual-review flow (a first rejection is 'pending_second_review').
  const triageState = pathway ? pathwayTriage(patient, pathway) : (patient?.triage || {});
  const triageComplete = triageState?.status === 'complete';

  // A referral is judged on its own forms alone (TDI-495); the strengths and
  // needs forms belong to the SEND pathway, which classifies separately.
  const schoolDone = isRoleReferralFormsComplete('school_user', formProgress, intakeConcern);
  const guardianDone = isRoleReferralFormsComplete('guardian', formProgress, intakeConcern);
  const bothReferralHfpDone = schoolDone && guardianDone;
  const allTriageDone = triageFormsAssigned && areAllTriageFormsComplete(formProgress);

  // ── Post-triage (stages 5-7) ──────────────────────────────────────────────
  // Classified by the number and outcome of clinician triage reviews:
  //   - 2 reviews: accepted if either accepted, else discharged.
  //   - 1 review, accepted → accepted.
  //   - 1 review, rejected → discharged if triage is complete (ADHD single
  //     decision), else pending-2nd-review (autism awaiting a second opinion).
  // The settled outcome then decides only which of the two terminal lists the
  // referral eventually lands in — outcomeList below places it.
  //
  // A decided referral stays in Pending Review until its outcome letter is
  // signed off, then waits in Admin Handover until the handover is complete,
  // then lands in Accepted or Not Accepted (Tom King, 23 and 29 Sept). Sign-off
  // is what makes it the admin team's, so a referral is always in exactly one
  // list. Accepted or not, every outcome takes this route: a not-accepted
  // letter is signed off by the deciding clinician like any other (T-22,
  // R-026).
  const outcomeList = (accepted) => {
    // Recording the decision is not the end of the clinician's task: the AI
    // outputs still have to be approved, the outcome letter generated and
    // signed off. Until sign-off the referral stays in Pending Review, because
    // the person it is waiting on is still the clinician (Tom King, 29 Sept).
    //
    // It used to show its outcome the moment the decision was recorded, which
    // said the referral was finished while real work was outstanding, and then
    // moved it BACKWARDS into Admin Handover on sign-off (Eve, 28 Sept:
    // "didn't go to admin handover first, just immediately to accepted").
    if (!hasSignedOffLetter(patient, pathway)) return 'pending-1st-review';
    // Sign-off hands the referral to the admin team; finishing the EMIS work
    // is what completes it (TDI-391). A sent letter alone leaves the child in
    // Admin Handover, because the list is the admin team's work queue and
    // posting the letter is not the end of that work.
    return isHandoverComplete(patient, pathway)
      ? (accepted ? 'waiting-list' : 'discharged-referrals')
      : 'ready-for-handover';
  };

  if (reviews.length >= 2) {
    return outcomeList(reviews.some((r) => r.accepted));
  }
  if (reviews.length === 1) {
    if (reviews[0].accepted) return outcomeList(true);
    // ADHD makes one terminal decision (no second review), so a rejection is
    // terminal; only the autism dual-review flow parks a first rejection in
    // Pending 2nd Review (its pathway status is not yet 'complete').
    if (!triageComplete) return 'pending-2nd-review';
    // A rejected single-decision (ADHD) referral waits for its letter's
    // sign-off in the deciding clinician's list, exactly as an acceptance does.
    return outcomeList(false);
  }

  // ── Ready-for-triage gate (LSC-038/LSC-044) ────────────────────────────────
  // Form completion alone never places a referral in the triage queue: a
  // provider user must actively mark it Ready for triage (rmsMarkReadyForTriage,
  // audited, with a recorded reason when forms are incomplete). The server-side
  // gate in rmsStartTriage enforces the same rule. A record already locked for
  // triage keeps its queue position, so pre-gate in-flight records still
  // classify where clinicians expect them.
  const readyForTriage = patient?.triageReadiness?.status === 'ready';
  if (readyForTriage || triageState?.status === 'in_triage') {
    return 'pending-1st-review';
  }

  // ── Forms all in: promote without anyone clicking ─────────────────────────
  // Completion is the trigger (Tom King, 23 Sept) for records that predate the
  // confirm-and-submit step. Records created since (TDI-418) are promoted by
  // their respondents' confirmations instead — the confirmation is what
  // submits the referral, so completion alone must not move it. The server
  // applies the same rule in _maybe_auto_mark_ready / _referral_ready_without_marker.
  if (
    bothReferralHfpDone &&
    allTriageDone &&
    (!recordRequiresSubmissionConfirmation(patient) || allRequiredRespondentsConfirmed(patient))
  ) {
    return 'pending-1st-review';
  }

  // ── Stage 1: forms outstanding (default) ──────────────────────────────────
  // At least one requested form is incomplete: referral/HFP forms for either
  // respondent, triage forms not yet assigned, or assigned triage forms still
  // in progress. The referral can still be marked Ready for triage from the
  // patient overview, with the reason recorded.
  return 'incomplete-referral-forms';
}

/**
 * Summarise the terminal triage decision for display (TD-141): the recorded
 * outcome and who decided it. The outcome is null until triage is complete —
 * a first rejection (awaiting a second opinion) is not terminal and is surfaced
 * separately. Once complete, the referral is "accepted" if any reviewer
 * accepted, otherwise "discharged". The decider is the accepting reviewer when
 * accepted, otherwise the most recent (finalising) reviewer.
 *
 * @param {Array} triageReviews - the patient's triageReviews array
 * @param {{ triageComplete?: boolean }} [opts]
 * @returns {{ outcome: 'accepted'|'discharged'|null, deciderName: string|null, decidedAt: any|null }}
 */
export function getTriageDecisionSummary(triageReviews, { triageComplete } = {}) {
  const reviews = Array.isArray(triageReviews) ? triageReviews : [];
  const acceptedReview = reviews.find((r) => r?.accepted === true) || null;

  let outcome = null;
  if (triageComplete) {
    outcome = acceptedReview ? 'accepted' : (reviews.length > 0 ? 'discharged' : null);
  }

  const decisiveReview = acceptedReview || reviews[reviews.length - 1] || null;
  return {
    outcome,
    deciderName: decisiveReview?.decidedBy?.clinicianName || null,
    decidedAt: decisiveReview?.decidedAt || null,
  };
}

/**
 * A short triage status flag for one pathway, so a combined (ADHD + autism)
 * referral can show each pathway independently on the clinician overview
 * (TD-129 AC3, e.g. "ADHD: Accepted · Autism: In triage"). Derived from that
 * pathway's own reviews + lock/status object, so one pathway being decided does
 * not affect the other's label.
 *
 * A picked-up referral reads "In review", with the holder's name only for a
 * viewer allowed to see it (TDI-703; the list's own wording, LSC-040). A
 * referral nobody holds reads "Ready for triage" once it is marked ready, and
 * "Not started" while forms are outstanding.
 *
 * @param {Array} reviews - that pathway's reviews (e.g. pathwayReviews(patient, pathway))
 * @param {object} triage - that pathway's lock/status object (e.g. pathwayTriage(patient, pathway))
 * @param {boolean} [readyForTriage] - the referral is marked ready (triageReadiness.status === 'ready')
 * @param {{ showAssigneeName?: boolean }} [options] - whether this viewer may
 *   see who holds the referral (the holder, Org Admins and Support Users)
 * @returns {{ label: string, tone: 'green'|'rose'|'amber'|'primary'|'gray' }}
 */
export function getPathwayTriageStatusLabel(reviews, triage, readyForTriage = false, { showAssigneeName = false } = {}) {
  const list = Array.isArray(reviews) ? reviews : [];
  const t = triage || {};
  const complete = t.status === 'complete';
  const { outcome } = getTriageDecisionSummary(list, { triageComplete: complete });
  if (outcome === 'accepted') return { label: 'Accepted', tone: 'green' };
  // Never "Discharged" (LSC-015): a triage decision is not a completed
  // assessment, and on a combined referral "ADHD: Discharged" read as having
  // been discharged from the full ADHD pathway. The stored outcome key stays
  // 'discharged'; only the label names the triage decision.
  if (outcome === 'discharged') return { label: 'Not accepted at triage', tone: 'rose' };
  // Exactly one rejection recorded (and not yet complete) → awaiting a 2nd opinion.
  if (list.length === 1 && list[0]?.accepted === false) {
    return { label: 'Awaiting 2nd opinion', tone: 'amber' };
  }
  if (t.status === 'in_triage' && t.assignedClinicianId) {
    return { label: inReviewLabel(t.assignedClinicianName, showAssigneeName), tone: 'primary' };
  }
  if (readyForTriage) return { label: 'Ready for triage', tone: 'primary' };
  return { label: 'Not started', tone: 'gray' };
}

/**
 * "In review — {name}" when the viewer may see who holds the referral and a
 * name is stored, else "In review" (TDI-703: the dashboard list's wording, so
 * the overview and the list say the same thing).
 */
export function inReviewLabel(assigneeName, showAssigneeName) {
  const name = String(assigneeName || '').trim();
  return showAssigneeName && name ? `In review — ${name}` : 'In review';
}

/**
 * Plain-language referral status for the service-user portal (Legal Guardian /
 * school rep), derived from the same triage signals the provider side uses
 * (TD-14 / B6). Labels only — the parent-facing wording is centralised here so
 * it can be adjusted in one place pending the B6 product/clinician sign-off.
 *
 * Precedence: a terminal triage decision (accepted / discharged) wins; then an
 * in-flight triage; then post-forms-awaiting-review; then awaiting the other
 * contributor; otherwise the referral is still in progress.
 *
 * @param {object} args
 * @param {Array} [args.triageReviews]
 * @param {string|null} [args.triageStatus] - patient.triage.status
 * @param {boolean} [args.triageFormsAssigned]
 * @param {boolean} [args.triageFormsComplete] - the guardian's triage forms all submitted
 * @param {boolean} [args.allFormsComplete] - this viewer's referral+HFP forms done
 * @param {boolean} [args.otherPartyComplete] - the other respondent's forms done
 * @returns {{ key: string, label: string }}
 */
export function getServiceUserReferralStatus({
  triageReviews,
  triageStatus,
  triageFormsAssigned,
  triageFormsComplete,
  allFormsComplete,
  otherPartyComplete,
} = {}) {
  const triageComplete = String(triageStatus || '') === 'complete';
  const { outcome } = getTriageDecisionSummary(triageReviews, { triageComplete });
  if (outcome === 'accepted') return { key: 'accepted', label: 'Accepted onto the waiting list' };
  if (outcome === 'discharged') return { key: 'closed', label: 'Referral closed' };

  const reviews = Array.isArray(triageReviews) ? triageReviews : [];
  const triageUnderway =
    triageStatus === 'in_triage' || triageStatus === 'pending_second_review' || reviews.length > 0;
  if (triageUnderway) return { key: 'under_review', label: 'Under clinical review' };

  if (triageFormsAssigned === true) {
    // Assigned is not done: on secondary referrals the triage forms are
    // auto-assigned the moment the guardian finishes the referral forms, so
    // the referral stays in progress until those forms are actually complete.
    if (triageFormsComplete !== true) {
      return { key: 'in_progress', label: 'Referral in progress' };
    }
    return { key: 'awaiting_review', label: 'Awaiting clinical review' };
  }
  if (allFormsComplete === true && otherPartyComplete === true) {
    return { key: 'awaiting_review', label: 'Awaiting clinical review' };
  }
  if (allFormsComplete === true && otherPartyComplete !== true) {
    return { key: 'awaiting_other', label: 'Awaiting the other contributor' };
  }
  return { key: 'in_progress', label: 'Referral in progress' };
}


// ─────────────────────────────────────────────────────────────────────────────
// Harbour SEN pathway (SEN & ND Access Portal, TDI-53)
// ─────────────────────────────────────────────────────────────────────────────
// The SEN pathway runs on the HFP forms alone — no referral forms, no triage
// forms. These helpers mirror calculateRoleStatus/isRoleReferralFormsComplete
// for that smaller set so SEN-only records read honestly on every surface
// (a guardian whose child is on the SEN pathway only must be able to reach
// "Completed" without referral forms that were never asked of them).

/**
 * Whether a patient record is a Harbour pupil on the SEN pathway with NO
 * referral attached. Legacy referral records always return false.
 *
 * @param {object|null|undefined} patient - patientRecords doc data
 * @returns {boolean}
 */
export function isSenOnlyRecord(patient) {
  if (!patient) return false;
  if (!isHarbourPupilRecord(patient)) return false;
  const referralActive = Boolean(patient.intakeConcern)
    || Boolean(patient.referralPathway && patient.referralPathway.startedAt);
  return !referralActive;
}

/**
 * Whether a role has submitted all SEN (HFP) forms.
 * @param {'school_user'|'guardian'} role
 * @param {Record<string, number>} formProgress
 * @param {object|null} [patient] - patientRecords doc data; Harbour pupils are judged on the v3 HFP set
 * @returns {boolean}
 */
export function areRoleSenFormsComplete(role, formProgress, patient, orgSetting = null) {
  if (!formProgress || !role) return false;
  const keys = hfpKeysForRoleData(role, formProgress, patient, orgSetting);
  return keys.length > 0 && keys.every((baseKey) => getPercent(formProgress, `${baseKey}_${role}`) >= 100);
}

/**
 * How many SEN (HFP) forms a role has completed out of the set.
 * @param {'school_user'|'guardian'} role
 * @param {Record<string, number>} formProgress
 * @param {object|null} [patient] - patientRecords doc data; the record's instrument picks the set
 * @param {'v2'|'v3'|'v4'|null} [orgSetting] - the organisation's setting, for a profile not yet started
 * @returns {{ completed: number, total: number }}
 */
export function senFormsProgress(role, formProgress, patient, orgSetting = null) {
  const keys = hfpKeysForRoleData(role, formProgress || {}, patient, orgSetting);
  let completed = 0;
  for (const baseKey of keys) {
    if (getPercent(formProgress || {}, `${baseKey}_${role}`) >= 100) completed++;
  }
  return { completed, total: keys.length };
}

/**
 * Display status for a role on a SEN-only record: the HFP forms are the whole
 * ask. Guardian pre-form states (not nominated / invite sent) read the same
 * way they do on referrals.
 *
 * @param {Object} args - role, formProgress, hasGuardian, guardianInviteStatus,
 *   and optionally `patient` (Harbour pupils are judged on the v3 HFP set)
 * @returns {string} STATUS.*
 */
export function calculateSenRoleStatus({ role, formProgress, hasGuardian, guardianInviteStatus, patient, orgSetting = null }) {
  if (role === 'guardian') {
    if (!hasGuardian) return STATUS.NOT_ASSIGNED;
    if (PENDING_PARTICIPANT_STATUSES.includes(guardianInviteStatus)) {
      return STATUS.INVITE_SENT;
    }
  }
  const keys = hfpKeysForRoleData(role, formProgress || {}, patient, orgSetting);
  const percents = keys.map((baseKey) => getPercent(formProgress || {}, `${baseKey}_${role}`));
  if (percents.length > 0 && percents.every((p) => p === 100)) return STATUS.COMPLETED;
  if (percents.some((p) => p > 0)) return STATUS.STARTED;
  return STATUS.NOT_STARTED;
}
